import {useState, useRef, useEffect} from 'react';
import {Routes, Route, Navigate, Outlet, useLocation} from 'react-router-dom';
import {
  UserContextProvider,
  RequireAuth,
  WeakAuth,
  useAuth,
} from '../contexts/UserContext';
import LoadingOverlay from 'react-loading-overlay';
import styled from 'styled-components';
import LoginPage from '../components/pages/LoginPage';
import HomePage from '../components/pages/HomePage';
import AnimationManager from '../libs/AnimationManager';
import {CustomDialogContextProvider} from '../contexts/CustomDialogContext';
import CustomDialog from '../components/organisms/CustomDialog';
import Footer from '../components/organisms/Footer';
import config from '../config';
import ForgotVenueCodePage from '../components/pages/ForgotVenueCodePage';
import RegistrationPage from '../components/pages/RegistrationPage';
import RegistrationThanksPage
  from '../components/pages/RegistrationPage/thanks';
import HeaderNav from '../components/organisms/HeaderNav';
import {AppContextProvider, useApp} from '../contexts/AppContext';
import VenueInformationPage from '../components/pages/VenueInformationPage';
import DevicesPage from '../components/pages/DevicesPage';
import PlaylistsPage from '../components/pages/PlaylistsPage';
import PlaylistDetailPage from '../components/pages/PlaylistDetailPage';
import TermsOfUsePage from '../components/pages/TermsOfUsePage';
import ContactPage from '../components/pages/ContactPage';
import FAQPage from '../components/pages/FAQPage';
import BetaFirmwareDLPage from '../components/pages/BetaFirmwareDLPage';
//import BetaFirmwareToUPage from '../components/pages/BetaFirmwareDLPage/tou';
import AgentLoginPage from '../components/pages/AgentLoginPage';
import ReportPage from '../components/pages/ReportPage';
import NewsPage from '../components/pages/NewsPage';
import ManualPage from '../components/pages/ManualPage';
import HowToRegisterPage from '../components/pages/HowToRegisterPage';
import RegistrationWizardPage from '../components/pages/RegistrationWizardPage';
import KUVOCareAboutPage from '../components/pages/KUVOCareAboutPage';
import KUVOCareApplyPage from '../components/pages/KUVOCareApplyPage';
import NotFoundPage from '../components/pages/NotFoundPage';
import ReportingStatusNowPlayingPage
  from '../components/pages/ReportingStatusPage';
import {
  ReportingStatusRoomTracksPage,
} from '../components/pages/ReportingStatusPage';
import ChartPage from '../components/pages/ChartPage';

import spinner from '../spinner.png';

const DataTypeUnit = {
  areas: {
    singular: 'AREA',
    plural: 'AREAS',
  },
  tracks: {
    singular: 'TRACK',
    plural: 'TRACKS',
  },
  hours: {
    singular: 'HOUR',
    plural: 'HOURS',
  },
  venues: {
    singular: 'VENUE',
    plural: 'VENUES',
  },
};

window.getUnitLabel = function(type, value) {
  let unit = DataTypeUnit[type] || null;
  if (!unit) {
    return '';
  }
  if (value == 1) {
    return unit.singular;
  }
  return unit.plural;
};

let autoScrollBlocked = false;
window.blockAutoScroll = function() {
  autoScrollBlocked = true;
};

const mq = window.matchMedia('screen and (min-width: 730px)');
window.isPC = function() {return !!mq.matches;};
window.isSP = function() {return !mq.matches;};

const LayoutWrapper = styled.div`

`;

const Content = styled.div`
  padding: 0 20px;
  position: relative;
  min-height: 780px;
  @media ${config.layout.wide} {
    min-height: 720px;
    padding: 0;
    max-width: 730px;
    margin: 0 auto;
  }
`;

const ContentInner = styled.div`
  min-height: 100vh;

  padding-bottom: 460px;

  &.registration-wizard {
    padding-bottom: 266px;

    @media ${config.layout.wide} {
      padding-bottom: 236px;
    }
  }
`;

if (/clubedit-stg/.test(
    `${window.location.host}`) || /venue-stg/.test(`${window.location.host}`)) {
  window.location.href = 'https://staging.kuvo.com/venue/';
} else if (/clubedit/.test(
    `${window.location.host}`) || /venue/.test(`${window.location.host}`)) {
  window.location.href = 'https://kuvo.com/venue/';
}

document.body.addEventListener('touchstart', function(event) {
  if (event.target.classList.contains('in_touch_target')) {
    event.target.classList.add('in_touch');
  } else {
    const target = event.target.closest('.in_touch_target');
    if (target && target.classList.contains('in_touch_target')){
      target.classList.add('in_touch');
    }
  }
});
document.body.addEventListener('touchend', function(event) {
  if (event.target.classList.contains('in_touch_target')) {
    event.target.classList.remove('in_touch');
  } else {
    const target = event.target.closest('.in_touch_target');
    if (target && target.classList.contains('in_touch_target')){
      target.classList.remove('in_touch');
    }
  }
});

//window.hideHeader = function () {
//  document.getElementsByTagName("body")[0].classList.remove("header");
//};

//window.showHeader = function () {
//  document.getElementsByTagName("body")[0].classList.add("header");
//};

export default function App() {
  AnimationManager.getSharedInstance();
  const loadingStateRef = useRef({
    count: 0,
  });
  const [showLoading, setShowLoading] = useState(false);

  window.incrementLoading = () => {
    loadingStateRef.current.count++;
    setShowLoading(0 < loadingStateRef.current.count);
  };
  window.decrementLoading = () => {
    loadingStateRef.current.count = Math.max(
        loadingStateRef.current.count - 1,
        0,
    );
    setShowLoading(0 < loadingStateRef.current.count);
  };
  window.clearLoading = () => {
    loadingStateRef.current.count = 0;
    setShowLoading(0 < loadingStateRef.current.count);
  };

  return (
      <AppContextProvider>
        <UserContextProvider>
          <CustomDialogContextProvider>
            <LoadingOverlay
                active={showLoading}
                spinner={
                  <img src={spinner} id={'loading-spinner'}/>
                }
                styles={{
                  wrapper: {
                    minHeight: '100vh',
                    backgroundColor: 'transparent',
                    position: 'relative',
                  },
                }}
            >
              <Routes>
                <Route path="/agent-login/:code" element={<AgentLoginPage/>}/>

                <Route element={<BaseLayout/>}>
                  <Route path="/registration/wizard/:step"
                         element={<RegistrationWizardPage/>}/>
                  <Route path="/registration/wizard"
                         element={<RegistrationWizardPage/>}/>

                  <Route path="/login" element={<LoginPage/>}/>
                  <Route
                      path="/forgot-venue-code/"
                      element={<ForgotVenueCodePage/>}
                  />
                  <Route
                      path="/registration/completed"
                      element={<RegistrationThanksPage/>}
                  />
                  <Route path="/registration/" element={<RegistrationPage/>}/>


                  <Route
                      element={
                        <WeakAuth>
                          <Outlet/>
                        </WeakAuth>
                      }
                  >
                    <Route path="/faq/" element={<FAQPage/>}/>
                    <Route path="/terms-of-use" element={<TermsOfUsePage/>}/>

                    <Route path="/contact/" element={<ContactPage/>}/>
                    <Route path="/manual/how-to-register/:step"
                           element={<HowToRegisterPage/>}/>
                    <Route path="/manual/how-to-register"
                           element={<HowToRegisterPage/>}/>
                    <Route path="/manual/" element={<ManualPage/>}/>
                  </Route>

                  <Route
                      element={
                        <RequireAuth>
                          <Outlet/>
                        </RequireAuth>
                      }
                  >
                    <Route path="/chart/" element={<ChartPage/>}/>
                    <Route path="/devices/" element={<DevicesPage/>}/>
                    <Route path="/venue-info/"
                           element={<VenueInformationPage/>}
                    />
                    <Route path="/playlists/:event_id"
                           element={<PlaylistDetailPage/>}
                    />
                    <Route path="/playlists/" element={<PlaylistsPage/>}/>
                    <Route path="/dashboard/" element={<ReportPage/>}/>
                    <Route path="/news/" element={<NewsPage/>}/>

                    <Route path="/care/about" element={<KUVOCareAboutPage/>}/>
                    <Route path="/care/apply/:customer_id"
                           element={<KUVOCareApplyPage/>}/>
                    <Route path="/reporting-status/rooms/"
                           element={<ReportingStatusRoomTracksPage/>}/>
                    <Route path="/reporting-status/"
                           element={<ReportingStatusNowPlayingPage/>}/>
                    <Route path="/" element={<HomePage/>}/>
                  </Route>

                  <Route path="*" element={<NotFoundPage/>}/>
                </Route>
              </Routes>
            </LoadingOverlay>
            <CustomDialog/>
          </CustomDialogContextProvider>
        </UserContextProvider>
      </AppContextProvider>
  );
}

let scrollTimer = null;
let scrollTargetHash = null;
let scrollTimeout = Date.now();

function getOffset(el) {
  var _x = 0;
  var _y = 0;
  while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
    _x += el.offsetLeft - el.scrollLeft;
    _y += el.offsetTop - el.scrollTop;
    el = el.offsetParent;
  }
  return {top: _y, left: _x};
}

function scrollWindowByTimer() {
  try {
    scrollTimer = null;
    if (!scrollTargetHash || scrollTimeout < Date.now()) {
      return;
    }
    if (window.initialFetching) {
      console.log(`Waiting initial fetching`);
      scrollTimer = setTimeout(scrollWindowByTimer, 50);
      return;
    }
    let elm = document.querySelector(`[data-hash="${scrollTargetHash}"]`);
    if (!elm) {
      console.log(`Waiting dom building`);
      scrollTimer = setTimeout(scrollWindowByTimer, 50);
      return;
    }
    scrollTimer = null;

    let top = getOffset(elm).top;
    let header = document.getElementById('nav-header');
    if (header) {
      let hr = header.getBoundingClientRect();
      top -= hr.height + 10;
    }
    scrollTargetHash = null;
    window.scrollTo(0, top);
  } catch (e) {
    console.error(e);
  }
}

function BaseLayout() {
  const {pathname, hash, search} = useLocation();
  useEffect(() => {
    clearTimeout(scrollTimer);
    scrollTimer = null;
    scrollTargetHash = null;
    if (autoScrollBlocked) {
      //console.log('Cancelled scrolling');
      setTimeout(() => {
        autoScrollBlocked = false;
        //console.log('Reset autoScrollBlocked flag');
      }, 500);
      return;
    }
    if (!hash) {
      window.scrollTo(0, 0);
      //console.log('HASH', hash);
    } else {
      //console.log('HASH', hash);
      scrollTargetHash = hash.replace(/^#/, '');
      scrollTimeout = Date.now() + 10 * 1000;
      scrollTimer = setTimeout(scrollWindowByTimer, 100);
    }
  }, [pathname, search]);

  let isRegistrationWizard = /wizard/.test(pathname || '');

  return (
      <>
        <HeaderNav/>
        <LayoutWrapper className="layout-wrapper" id={'layout-wrapper'}>
          <Content className="main-content">
            <ContentInner className={`main-content-inner ${isRegistrationWizard
                ? 'registration-wizard'
                : ''}`}>
              <Outlet/>
            </ContentInner>
          </Content>
        </LayoutWrapper>
        <Footer isRegistrationWizard={isRegistrationWizard}/>
      </>
  );
}
